<!-- eslint-disable max-len -->
<template>
  <aha-modal :visibleOverflow="true" @hide="hideModal()" :title="!currentAccount.id ? $i18nPresenter.t('Add Team members') : $i18nPresenter.t('Invite members')">
    <form @submit.prevent="onSubmitInvite()">
      <div v-if="!currentAccount.id" class="mb-3">
        <label class="font-bold">{{ $i18nPresenter.t('Team name') }}</label>
        <aha-input
          type="text"
          v-model="teamName"
          :max-length="100"
          @focus="focusTeamName"
          :placeholder="$i18nPresenter.t('Team Name')"
        />
      </div>
      <div>
        <label v-if="!currentAccount.id" class="font-bold">{{ $i18nPresenter.t('Invite members') }}</label>
        <div class="flex mb-2 add-member-input item-center">
          <AhaInputTag
            class="max-h-28 overflow-auto"
            id="input-tag__invite-new-member"
            v-model="emails"
            :placeholder="emails.length > 0 ? '' : inputTagPlaceHolder"
            :validator="emailRegex"
            :disabled="isDisabledInviteeInput"
            :allow-duplicate="false"
            :tagSplitterKeys="['Enter', 'Semicolon', 'Comma', 'Space']"
            :formatter="(entry) => entry.toLowerCase()"
          />
          <RoleSelection
            :style="{
              width: '80px',
              height: 'calc(100% - 3px)',
              position: 'absolute',
              top: '1px',
              right: '1px',
            }"
            :invitedRole="invitedRole"
            @changeInvitedRole="changeInvitedRole"
            :isInviteTeam="true"
            :absolute="true"
          />
        </div>
      </div>
      <div class="license-info">
        <div
          v-if="!isExpiredPlan && !isTeamPlanFree"
          class="mb-2"
          :class="{
            'text-green-100': modalData.numberOfUsedLicenses < modalData.licenseQuantity,
          }"
        >
          <span class="font-bold">{{ $i18nPresenter.t('Licenses used:') }}</span>
          <span>
            {{ modalData.numberOfUsedLicenses | formatNumber }}/{{ modalData.licenseQuantity | formatNumber }}
          </span>
        </div>
        <div
          v-if="isExpiredPlan"
          class="mb-2 font-bold text-red-50"
        >
          {{ $i18nPresenter.t('Your licences have expired') }}
        </div>
      </div>

      <div v-if="showInviteError">
        <span class="block text-red-100 text-xs mb-2">{{ inviteErrorListEmail }}</span>
      </div>
    </form>
    <div v-if="isShowTeamLimitExceededError">
      <a @click="handleUpgradeTeam()" class="font-bold text-primary-90 cursor-pointer hover:text-primary-100">
        {{ $i18nPresenter.t('Buy licenses') }}
      </a> {{ $i18nPresenter.t('for additional members') }}.
    </div>
    <template #action>
      <aha-link @click="hideModal()">
        <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
      </aha-link>

      <aha-antd-button
        ref="sendInvitationButton"
        class="ml-4"
        type="submit"
        :disabled="isDisabledSendButton"
        :submitting="isSendingInvitations"
        is-loading-inline
        v-aha-emit-action
        name="invite-new-member"
        @click="onSubmitInvite()"
      >
        {{ $i18nPresenter.t('Invite') }}
      </aha-antd-button>
    </template>
  </aha-modal>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import InvitationApi from '@/api/invitation'
import utils from '@/util'
import Config from '@/config'
import eventCategories from '@/constant/eventCategories'
import RoleSelection from '@/components-v2/my-team/RoleSelection.vue'
import recaptchaV3Service from '@/services/recaptcha-v3'

export default {
  components: {
    RoleSelection,
  },
  props: {
    modalData: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      emails: [],
      isSendingInvitations: false,
      showInviteError: false,
      inviteErrorListEmail: '',
      emailRegex: "^(([^<>()[\\]\\.,;:\\s@']+(\\.[^<>()[\\]\\.,;:\\s@']+)*)|('.+'))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$",
      keyRecaptcha: Config.keyRecapcha,
      tokenRecaptcha: '',
      invitedRole: 'editor',
      freeLicenseQuantity: 10,
      teamName: '',
      isCaptcharLoadError: false,
    }
  },
  computed: {
    ...mapGetters([
      'currentUserIsTeamOwnerOrAdmin',
      'isTeamCreatedByCustomerSuccess',
      'isPaidTeamPlan',
    ]),
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
    }),
    getTotalMemberText() {
      if (this.currentAccount && this.currentAccount.licenseQuantity) return this.currentAccount.licenseQuantity
      return 200
    },
    isDisabledInviteeInput() {
      if (this.currentAccount.accountId) {
        if (this.modalData.numberOfUsedLicenses + this.emails.length
          >= this.modalData.licenseQuantity) return true
        return false
      }
      return this.emails.length >= 200
    },
    isDisabledSendButton() {
      const hasInput = this.isInputTagHasValue || this.emails.length > 0
      if (this.currentAccount.accountId) {
        return !hasInput || (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity)
      }
      return !hasInput
    },
    isShowTeamLimitExceededError() {
      return (this.modalData.numberOfUsedLicenses >= this.modalData.licenseQuantity) ||
        this.inviteErrorListEmail == 'Hit maximum number of licenses'
    },

    isExpiredPlan() {
      return this.modalData.planEndAt && moment().isAfter(moment(this.modalData.planEndAt), 'minute')
    },
    isTeamPlanFree() {
      return this.modalData.plan === 'free' || !this.modalData.plan
    },
    inputTagPlaceHolder() {
      return this.$i18nPresenter.t('Emails, comma separated')
    },
  },
  mounted() {
    setTimeout(() => {
      this.focusInviteeInput()
    }, 150)
    this.teamName = `${this.currentUser.firstName} ${this.currentUser.lastName}'s Team`
  },
  methods: {
    ...mapActions([
      'createCustomerPortalAction',
      'requestAdditionalLicensePopupAction',
      'goToBulkLicensePageAction',
    ]),
    onVerify(response) {
      this.tokenRecaptcha = response
      this.handleSendInvitation()
    },
    async onSubmitInviteWithRecaptchaV3() {
      try {
        this.tokenRecaptcha = await recaptchaV3Service.executeRecaptcha('invite_member')
        if (!this.tokenRecaptcha) {
          this.isCaptcharLoadError = true
        }
        this.handleSendInvitation()

        utils.trackGA4AndMixpanel(
          'Invite Member',
          'reCAPTCHA v3 verified',
        )
      } catch (error) {
        this.isSendingInvitations = false

        Sentry.captureException(new Error('Presenter invite member with reCAPTCHA v3 error'), {
          extra: { error: error.message },
        })
      }
    },
    onSubmitInvite() {
      if (this.isSendingInvitations) return
      this.isSendingInvitations = true
      this.onSubmitInviteWithRecaptchaV3()
    },

    onRecaptchaError() {
      this.isSendingInvitations = false
    },
    focusInviteeInput() {
      const input = document.getElementById('input-tag__invite-new-member').children[0]
      if (!input) return
      input.focus()
    },

    handleClickOnFooterTypeForm() {
      let eventAction = ''
      if (this.currentAccount.id) {
        if (this.currentUserIsTeamOwnerOrAdmin) {
          eventAction = 'Team management > Invite modal > Owner clicks on typeform link'
        } else {
          eventAction = 'Team management > Invite modal > Casual user clicks on typeform link'
        }
      } else {
        eventAction = 'SideNav > Add team member > Contact our Sales\'s team'
      }
      utils.mixpanelTracking(utils.getGACategories(this.$route.name), eventAction, '', '', {
        userId: this.currentUser.id,
        plan: this.currentUser.plan,
      })
    },

    async handleSendInvitation() {
      if (!this.isInputTagHasValue && this.emails.length === 0) {
        utils.showToastError(this.$i18nPresenter.t('Please enter member\'s email'))
        this.focusInviteeInput()
        this.tokenRecaptcha = ''
        return
      }
      this.isSendingInvitations = true
      // Because we have 2 elements listen to the Enter key at the same
      // time (the form & the input tag),
      // setTimeout a bit for waiting the input tag (emails) to be added after Enter pressed
      setTimeout(async () => {
        try {
          const eventCategory = utils.getGACategories(this.$route.name)
          let eventAction = 'SideNav > Add team member > Send invitation'
          let eventLabel = this.emails.toString()
          let eventParams = {
            plan: this.currentUser.plan,
            userId: this.currentUser.id,
            roleInvited: this.invitedRole,
          }
          if (this.currentAccount.id) {
            eventAction = 'Team management > Invite member > Send invitation'
          }
          utils.mixpanelTracking(
            eventCategory,
            eventAction,
            eventLabel,
            '',
            eventParams,
          )
          const payload = {
            emails: this.emails.map((email) => email.toLowerCase()),
            tokenRecaptcha: this.tokenRecaptcha,
            accountRole: this.invitedRole,
            teamName: this.teamName,
          }
          payload.isRecaptchaV3 = true
          if (this.isCaptcharLoadError) {
            payload.bypassRecaptcha = true
          }
          const res = await InvitationApi.createInvitations(payload)
          this.$emit('hide')
          this.$emit('updateInvitations', this.emails.length)

          eventAction = 'SideNav > Create a team > Success'
          eventLabel = this.teamName
          eventParams = {
            userId: this.currentUser.id,
            plan: this.currentUser.plan,
            teamId: res[0].accountId,
          }
          if (this.currentAccount.id) {
            eventAction = 'Team management > Invite member > Invited successfully'
            eventLabel = this.emails.toString()
            eventParams.role = this.invitedRole
          }
          utils.mixpanelTracking(
            eventCategory,
            eventAction,
            eventLabel,
            '',
            eventParams,
          )
          this.tokenRecaptcha = ''
          utils.showToastSuccess(this.$i18nPresenter.t('Invited successfully'))
          window.location.href = `${window.location.origin}/apps/team/members`
        } catch (error) {
          Sentry.captureException(error)
          utils.mixpanelTracking(
            eventCategories.MY_TEAM,
            'Team management > Invite member > Invite failed',
            error.body.message,

          )
          this.showInviteError = true
          if (error.body.emails) {
            if (this.$store.state.currentAccount.createdFrom === 'presenter') {
              this.inviteErrorListEmail = `${this.$i18nPresenter.t('This emails is already has team:')} ${error.body.emails.join(', ')}`
            } else this.inviteErrorListEmail = `${this.$i18nPresenter.t('This emails is already has team or plan:')} ${error.body.emails.join(', ')}`
          } else if (error.body.message) this.inviteErrorListEmail = error.body.message
          else utils.showToastError(this.$i18nPresenter.t('Something went wrong, please check your connection or contact us for support'))
          this.tokenRecaptcha = ''
        } finally {
          this.isSendingInvitations = false
        }
      }, 300)
    },
    hideModal() {
      utils.mixpanelTracking(
        eventCategories.MY_TEAM,
        'Team management > Invite member > Cancel invitation',
        this.emails.toString(),
      )
      this.$emit('hide')
    },
    contactSupport() {
      window.open('https://form.typeform.com/to/sC0I4AXE', '_blank')
    },
    changeInvitedRole(value) {
      this.invitedRole = value
      if (this.currentAccount.id) {
        utils.trackGA4AndMixpanel('Select role', {
          eventAction: 'Team management > Invite member > Select role',
          eventCategory: 'My Team',
          eventLabel: value,
        })
      } else {
        utils.mixpanelTracking(utils.getGACategories(this.$route.name), 'SideNav > Add team member > Select role', value, '', {
          userId: this.currentUser.id,
          plan: this.currentUser.plan,
        })
      }
    },
    focusTeamName() {
      utils.mixpanelTracking(utils.getGACategories(this.$route.name), 'SideNav > Add team member > Team name > Focus input')
    },
    handleUpgradeTeam() {
      this.hideModal()
      utils.trackGA4AndMixpanel('Team management > Invite member > Buy licenses', {
        eventCategory: eventCategories.MY_TEAM,
        eventAction: 'Team management > Invite member > Buy licenses',
      })

      if (this.isTeamCreatedByCustomerSuccess) {
        this.requestAdditionalLicensePopupAction(true)
      } else if (this.isPaidTeamPlan) {
        this.createCustomerPortalAction({
          isUpdatingPlan: true,
        })
      } else {
        this.goToBulkLicensePageAction()
      }
    },
  },
}
</script>
<style scoped lang='scss'>
.add-member-input  {
  position: relative;
}
</style>

<style>
#input-tag__invite-new-member:hover, #input-tag__invite-new-member.\--focused {
  background-color: #fff;
  padding-right: 80px;
}
</style>
